import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { StatCard, Card, Btn, Spinner, Badge, useToast } from '../components/shared/UI';
import OnboardingChecklist from '../components/dashboard/OnboardingChecklist';
import { AreaChart, BarChart, DonutChart, HorizontalBars, StackedBar } from '../components/shared/Charts';
import { fmtCurrency, fmtDate, getDocType, getStatus, DOC_TYPES, STATUS_OPTIONS } from '../utils/helpers';
import api from '../utils/api';

const STATUS_COLORS = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s.color]));
const TYPE_COLORS   = Object.fromEntries(DOC_TYPES.map(t => [t.value, t.color]));

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats]   = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const { show, ToastContainer } = useToast();

  useEffect(() => {
    Promise.all([
      api.get('/documents/stats'),
      api.get('/documents?limit=6')
    ]).then(([s, r]) => {
      setStats(s.data);
      setRecent(r.data.slice(0, 6));
    }).finally(() => setLoading(false));
  }, []);

  const quickCreate = type => navigate(`/documents/new?type=${type}`);

  if (loading) return <Spinner />;

  // Chart data shapes
  const revenueSeries = (stats?.monthlyRevenue || []).map(m => ({
    label: m.label, value: m.revenue,
  }));
  const statusData = (stats?.byStatus || []).map(s => ({
    label: getStatus(s.status).label,
    value: s.count,
    color: STATUS_COLORS[s.status] || '#94a3b8',
  })).filter(d => d.value > 0);
  const typeData = (stats?.byType || []).map(t => ({
    label: getDocType(t.type).label,
    value: t.count,
    color: TYPE_COLORS[t.type] || '#64748b',
  })).filter(d => d.value > 0);
  const topClients = (stats?.topClients || []).map(c => ({
    label: c.name || 'Unknown',
    value: c.revenue,
  }));

  // Document count per month (last 12)
  const monthlyCountSeries = (stats?.monthlyCounts || []).map(m => ({
    label: m.label, value: m.count,
  }));

  // A/R aging — labels & colors
  const AGING_META = [
    { key: 'current', label: 'Current',     color: '#10B981' },
    { key: '1-30',    label: '1–30 days',   color: '#3B82F6' },
    { key: '31-60',   label: '31–60 days',  color: '#F59E0B' },
    { key: '61-90',   label: '61–90 days',  color: '#F97316' },
    { key: '90+',     label: '90+ days',    color: '#EF4444' },
  ];
  const agingSegments = AGING_META.map(m => {
    const found = (stats?.aging || []).find(a => a.bucket === m.key);
    return {
      label: m.label, color: m.color,
      value: found?.amount || 0,
      count: found?.count  || 0,
    };
  });

  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--heading)', letterSpacing: '-0.5px' }}>
          Good day, {user?.name?.split(' ')[0]} 👋
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>Here's your business overview</p>
      </div>

      <ToastContainer />
      <OnboardingChecklist show={show} />

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Quotations" value={stats?.quotations || 0} icon="📋" color="#2E75B6" />
        <StatCard label="Invoices" value={stats?.invoices || 0} icon="🧾" color="#10B981" />
        <StatCard label="Bills" value={stats?.bills || 0} icon="💳" color="#F59E0B" />
        <StatCard label="Total Received" value={fmtCurrency(stats?.totalPaid)} icon="💰" color="#8B5CF6" />
        <StatCard label="Outstanding" value={fmtCurrency(stats?.outstanding)} icon="⏳" color="#F97316" />
        <StatCard label="Overdue" value={stats?.overdue || 0} icon="⚠️" color="#EF4444" />
        <StatCard label="Avg Days to Pay"
          value={stats?.avgDaysToPay != null ? `${stats.avgDaysToPay} days` : '—'}
          icon="📅" color="#0EA5E9" />
      </div>

      {/* Revenue chart — full width */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h2 style={chartH}>Revenue (last 12 months)</h2>
            <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>Sum of paid documents per month</p>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Total: <b style={{ color: 'var(--text)' }}>{fmtCurrency(revenueSeries.reduce((s, d) => s + d.value, 0))}</b>
          </div>
        </div>
        <AreaChart data={revenueSeries} formatY={v => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(0)} />
      </Card>

      {/* A/R Aging — full width, high signal */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h2 style={chartH}>Receivables Aging</h2>
            <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>Outstanding invoices by days past due</p>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Outstanding: <b style={{ color: 'var(--text)' }}>{fmtCurrency(stats?.outstanding)}</b>
            <span style={{ marginInlineStart: 8, color: 'var(--text-subtle)' }}>
              ({stats?.outstandingCount || 0} invoice{stats?.outstandingCount === 1 ? '' : 's'})
            </span>
          </div>
        </div>
        <StackedBar segments={agingSegments} formatValue={v => fmtCurrency(v)} />
      </Card>

      {/* Two-column charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 24, marginBottom: 24 }}>
        <Card>
          <h2 style={chartH}>Documents by Status</h2>
          <div style={{ marginTop: 16 }}>
            <DonutChart data={statusData} centerLabel="Total" />
          </div>
        </Card>
        <Card>
          <h2 style={chartH}>Documents by Type</h2>
          <div style={{ marginTop: 16 }}>
            <DonutChart data={typeData} centerLabel="Total" />
          </div>
        </Card>
      </div>

      {/* Documents per month */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h2 style={chartH}>Documents Created (last 12 months)</h2>
            <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>All document types per month</p>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Total: <b style={{ color: 'var(--text)' }}>{monthlyCountSeries.reduce((s, d) => s + d.value, 0)}</b>
          </div>
        </div>
        <BarChart data={monthlyCountSeries} formatY={v => v.toFixed(0)} color="#8B5CF6" />
      </Card>

      {/* Top clients + Quick create */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 24, marginBottom: 24 }}>
        <Card>
          <h2 style={chartH}>Top Clients by Revenue</h2>
          <div style={{ marginTop: 14 }}>
            <HorizontalBars data={topClients} formatValue={v => fmtCurrency(v)} color="var(--blue)" maxRows={5} />
          </div>
        </Card>
        <Card>
          <h2 style={chartH}>Quick Create</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
            {DOC_TYPES.map(t => (
              <button key={t.value} onClick={() => quickCreate(t.value)}
                style={{
                  padding: '12px', border: `1px solid ${t.color}30`, borderRadius: 10,
                  background: t.color + '08', cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s',
                  fontFamily: 'inherit'
                }}
                onMouseOver={e => e.currentTarget.style.background = t.color + '15'}
                onMouseOut={e => e.currentTarget.style.background = t.color + '08'}
              >
                <span style={{ fontSize: 18 }}>{t.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{t.label}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent documents */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={chartH}>Recent Documents</h2>
          <Btn variant="ghost" size="sm" onClick={() => navigate('/documents')}>View all</Btn>
        </div>
        {recent.length === 0
          ? <p style={{ fontSize: 13, color: 'var(--text-subtle)', textAlign: 'center', padding: '24px 0' }}>No documents yet</p>
          : recent.map(doc => {
            const st = getStatus(doc.status);
            const dt = getDocType(doc.type);
            return (
              <div key={doc._id} onClick={() => navigate(`/documents/${doc._id}`)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 0', borderBottom: '1px solid var(--border-light)', cursor: 'pointer',
                  gap: 12
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{dt.icon}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {doc.docNumber}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
                      {doc.clientSnapshot?.name || doc.clientSnapshot?.company || 'No client'} · {fmtDate(doc.issueDate)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{fmtCurrency(doc.total, doc.currencySymbol)}</span>
                  <Badge label={st.label} color={st.color} />
                </div>
              </div>
            );
          })
        }
      </Card>
    </div>
  );
}

const chartH = { fontSize: 15, fontWeight: 600, color: 'var(--heading)', margin: 0 };
